import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';

const service = {
  createPeriod: data => httpRequest.post('api/workflows/periods', data),
  createAIFMPeriod: data => httpRequest.post(`api/aifm/period/${data.id}`, data.body),
  createPeriodAutomatic: data => httpRequest.post('api/workflows/periods/automatic', data),
  convertAutomationReport: ({ periodId, workflowId }) =>
    httpRequest.post(
      `${periodId}/conversion/parse`,
      {},
      {
        headers: {
          workflow_id: workflowId
        }
      }
    ),
  updatePeriod: ({ periodId, data }) => httpRequest.put(`api/workflows/periods/${periodId}`, data),
  getPeriodFiles: (periodId, params) =>
    httpRequest.get(`api/periods/${periodId}/files`, { params }),
  getWorkflowsPeriods: periodId => httpRequest.get(`api/workflows/periods/${periodId}`),
  getPeriodById: (periodId, params) =>
    httpRequest.get(`api/workflows/periods/${periodId}`, { ...params }),
  deletePeriodById: periodId => httpRequest.delete(`api/workflows/periods/${periodId}`),
  deletePeriodFile: data => httpRequest.delete(`api/periods/${data.periodId}/files/${data.fileId}`),
  mutateTransformationNote: ({ data, transformId, stepId }) =>
    httpRequest.put(`api/${transformId}/edit-note/${stepId}`, data),
  uploadFiles: ({ periodId, data, onUploadProgress }) =>
    httpRequest.post(`api/periods/${periodId}/files`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }),
  updateUploadedFile: ({ periodId, fileId, data, onUploadProgress }) =>
    httpRequest.put(`api/periods/${periodId}/files/${fileId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }),
  reRunAiActions: ({ transformationId, stepId, data }) =>
    httpRequest.post(`/api/retry-smart-transformation/${transformationId}/${stepId}`, data)
};

export const useCreatePeriod = ({ mutationOptions } = {}) => {
  return useMutation(service.createPeriod, mutationOptions);
};

export const useCreateAIFMPeriod = ({ mutationOptions } = {}) => {
  return useMutation(service.createAIFMPeriod, mutationOptions);
};

export const useCreatePeriodAutomatic = ({ mutationOptions } = {}) => {
  return useMutation(service.createPeriodAutomatic, mutationOptions);
};

export const useDeletePeriodFile = ({ mutationOptions } = {}) => {
  return useMutation(service.deletePeriodFile, mutationOptions);
};

export const useDeletePeriod = ({ mutationOptions } = {}) => {
  return useMutation(service.deletePeriodById, mutationOptions);
};

export const usePeriodFiles = ({ queryOptions, periodId, params = {} } = {}) => {
  return useQuery(
    ['GET_PERIOD_FILES', periodId, params],
    () => service.getPeriodFiles(periodId, params),
    queryOptions
  );
};

export const useGetWorklflowsPeriods = ({ queryOptions, periodId } = {}) => {
  return useQuery(
    ['GET_WORKFLOWS_PERIODS', periodId],
    () => service.getWorkflowsPeriods(periodId),
    queryOptions
  );
};

export const usePeriodById = ({
  queryOptions,
  queryParams,
  periodId,
  customKey = '',
  params = {}
} = {}) => {
  return useQuery(
    ['GET_PERIOD_BY_ID', customKey, periodId],
    () => service.getPeriodById(periodId, params),
    queryParams ?? queryOptions
  );
};

export const useConvertReport = ({ mutationOptions } = {}) => {
  return useMutation(service.convertAutomationReport, mutationOptions);
};

export const useUploadPeriodFiles = ({ mutationOptions } = {}) => {
  return useMutation(service.uploadFiles, mutationOptions);
};

export const useUpdateUploadedFile = ({ mutationOptions } = {}) => {
  return useMutation(service.updateUploadedFile, mutationOptions);
};

export const useUpdatePeriod = () => {
  return useMutation(service.updatePeriod);
};

export const useMutateTransformationNote = ({ mutationOptions } = {}) => {
  return useMutation(service.mutateTransformationNote, mutationOptions);
};

export const useReRunAiActions = ({ mutationOptions } = {}) => {
  return useMutation(service.reRunAiActions, mutationOptions);
};
